import { useState, type ComponentProps } from 'react';

import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Dialog } from '../../components/ui/Dialog';
import { DropdownMenu, type DropdownMenuItem } from '../../components/ui/DropdownMenu';
import { FieldHelp } from '../../components/ui/FieldHelp';
import { Input } from '../../components/ui/Input';
import { Link } from '../../components/ui/Link';
import { ModuleSidebar } from '../../components/ui/ModuleSidebar';
import { PageBreadcrumb, PageContentCard, PageTabs } from '../../components/ui/ModulePage';
import { Popover } from '../../components/ui/Popover';
import { ProductNavigation } from '../../components/ui/ProductNavigation';
import { findProductModule } from '../../components/ui/ProductNavigation/navigation-data';
import { Table, type TableColumn, type TableSortOrder } from '../../components/ui/Table';
import { filterStudents, maskStudentPhone, type Student, type StudentFilters } from '../../data/students';
import { StudentAvatar } from './StudentAvatar';
import styles from './Page.module.css';

const frontDesk = findProductModule('front-desk');
const defaultFilters = { formal: true, nonFormal: true, includeOtherCampuses: true };
const studentActionItems: readonly DropdownMenuItem[] = [
  { id: 'view', label: '查看学员信息' },
  { id: 'seat', label: '选择上课座位', group: '教学安排', disabled: true },
  { id: 'advisor', label: '指派学管师', group: '教学安排', disabled: true },
  { id: 'assign-class', label: '分班', group: '教学安排', disabled: true },
  { id: 'one-to-one', label: '一对一排课', group: '教学安排', disabled: true },
  { id: 'trial', label: '跟班试听', group: '教学安排', disabled: true },
  { id: 'adjust-class', label: '调班', group: '班级与校区', disabled: true },
  { id: 'transfer-class', label: '一键转班', group: '班级与校区', disabled: true },
  { id: 'transfer-campus', label: '转校', group: '班级与校区', disabled: true },
  { id: 'class-history', label: '学员进出班级记录', group: '班级与校区', disabled: true },
  { id: 'leave', label: '请假', group: '学籍与考勤', disabled: true },
  { id: 'pause-resume', label: '停课/复课', group: '学籍与考勤', disabled: true },
  { id: 'suspend', label: '休学', group: '学籍与考勤', disabled: true },
  { id: 'withdraw', label: '退学', group: '学籍与考勤', disabled: true },
  { id: 'edit', label: '修改', group: '资料管理', disabled: true },
  { id: 'delete', label: '删除', group: '资料管理', separatorBefore: true, danger: true, disabled: true },
];

function StudentActionTrigger(props: ComponentProps<typeof Link>) {
  return <Link {...props} type="primary" />;
}
const optionalFields = [
  { key: 'account', label: '师生信账号' }, { key: 'studentNo', label: '学号' },
  { key: 'identityNo', label: '身份证号' }, { key: 'phone', label: '手机号码' },
  { key: 'englishName', label: '英文名' }, { key: 'cardNo', label: '卡号' },
  { key: 'school', label: '公立学校' }, { key: 'status', label: '学员状态' },
] as const;

function exportStudents(rows: readonly Student[]) {
  const csvValue = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const data = [
    ['姓名', '师生信账号', '学号', '身份证号', '手机号码', '英文名', '卡号', '公立学校', '学员状态', '校区'],
    ...rows.map((student) => [student.name, student.account, student.studentNo, student.identityNo, maskStudentPhone(student.phone), student.englishName, student.cardNo, student.school, student.status, student.campus]),
  ].map((row) => row.map(csvValue).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['\uFEFF', data], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = '学员信息-模拟数据.csv';
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function StudentManagementPage() {
  const [keyword, setKeyword] = useState('');
  const [filters, setFilters] = useState<StudentFilters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedKeys, setSelectedKeys] = useState<readonly (number | string)[]>([]);
  const [sort, setSort] = useState<{ key: string; order: TableSortOrder }>({ key: '', order: 'asc' });
  const [visibleFields, setVisibleFields] = useState<readonly string[]>(optionalFields.map((field) => field.key));
  const [detail, setDetail] = useState<Student | null>(null);
  const rows = filterStudents(filters);
  if (sort.key) rows.sort((a, b) => String(a[sort.key as keyof Student]).localeCompare(String(b[sort.key as keyof Student]), 'zh-CN', { numeric: true }) * (sort.order === 'asc' ? 1 : -1));

  function changeFilters(next: StudentFilters) {
    setFilters(next);
    setPage(1);
    setSelectedKeys([]);
  }

  const columns: TableColumn<Student>[] = [
    { key: 'name', title: '姓名', fixed: 'left', width: 128, sortable: true, render: (student) => <Link aria-description={`性别：${student.gender}`} className={styles.nameLink} onClick={() => setDetail(student)}><StudentAvatar student={student} /><span>{student.name}</span></Link> },
    { key: 'account', title: '师生信账号', width: 144, sortable: true, ellipsis: true },
    { key: 'studentNo', title: '学号', width: 104, sortable: true },
    { key: 'identityNo', title: '身份证号', width: 112, sortable: true, render: (student) => student.identityNo || '—' },
    { key: 'phone', title: '手机号码', width: 136, sortable: true, render: (student) => maskStudentPhone(student.phone) },
    { key: 'englishName', title: '英文名', width: 96, sortable: true },
    { key: 'cardNo', title: '卡号', width: 112, sortable: true, render: (student) => student.cardNo || '—' },
    { key: 'school', title: '公立学校', width: 152, sortable: true, ellipsis: true },
    { key: 'status', title: '学员状态', fixed: 'right', width: 104, sortable: true, render: (student) => <span className={styles.statusText}><span aria-hidden="true" className={styles.statusDot} data-status={student.status} />{student.status}</span> },
    { key: 'actions', title: '操作', fixed: 'right', width: 88, render: (student) => <DropdownMenu
      ariaLabel={`${student.name}的操作`}
      items={studentActionItems}
      onItemSelect={(item) => { if (item.id === 'view') setDetail(student); }}
      trigger={<StudentActionTrigger>操作</StudentActionTrigger>}
    /> },
  ];

  return <main className={styles.page}>
    <ProductNavigation defaultActive="前台业务" />
    <div className={styles.workspace}>
      <ModuleSidebar activeItem="学员信息管理" ariaLabel="前台业务导航" groups={frontDesk?.groups ?? []} />
      <section aria-label="学员信息管理页面" className={styles.main}>
        <h1 className={styles.visuallyHidden}>学员信息管理</h1>
        <PageBreadcrumb current="学员信息管理" items={['前台业务']} />
        <PageContentCard className={styles.contentCard}>
          <PageTabs ariaLabel="学员管理" onValueChange={() => undefined} panelClassName={styles.panel} value="student-info" tabs={[
            { id: 'student-info', label: '学员信息管理', panel: <>
              <form aria-label="学员查询" className={styles.filters} onSubmit={(event) => { event.preventDefault(); changeFilters({ ...filters, keyword }); }}>
                <div className={styles.keywordGroup}>
                  <Input allowClear ariaLabel="学员查询关键词" className={styles.search} onValueChange={setKeyword} placeholder="学员姓名及首字母/学号/电话" value={keyword} />
                  <FieldHelp content="支持学员姓名、姓名拼音首字母、学号或电话号码，点击查询或按回车生效。" label="学员查询说明" />
                </div>
                <Button htmlType="submit">查询</Button>
                <Button onClick={() => { setKeyword(''); changeFilters(defaultFilters); setSort({ key: '', order: 'asc' }); }} type="default">重置</Button>
                <Button disabled={rows.length === 0} onClick={() => exportStudents(rows)} type="default">导出</Button>
                <div className={styles.filterGroup}>
                  <Checkbox checked={filters.formal ?? true} label="正式学员" onCheckedChange={(formal) => changeFilters({ ...filters, formal })} />
                  <Checkbox checked={filters.nonFormal ?? true} label="非正式学员" onCheckedChange={(nonFormal) => changeFilters({ ...filters, nonFormal })} />
                  <FieldHelp content="试听为非正式学员；在读、休学、退学为正式学员。意向学员在专门的意向学员列表管理。" label="学员类型说明" />
                </div>
                <div className={styles.campusFilter}>
                  <Checkbox checked={filters.includeOtherCampuses ?? true} label="包含跨校区学员" onCheckedChange={(includeOtherCampuses) => changeFilters({ ...filters, includeOtherCampuses })} />
                  <FieldHelp content="关闭后只显示当前岳麓校区的学员。" label="跨校区学员说明" />
                </div>
              </form>
              <div aria-label="学员操作" className={styles.toolbar} role="toolbar">
                <div className={styles.actions}>
                  <Button disabled>到校签到</Button>
                  <Button disabled type="default">离校签退</Button>
                  <Button disabled type="default">按课程一键转班</Button>
                  <Button disabled type="default">合并学员</Button>
                  <Button disabled trailingIcon="a-arrow-down" type="default">安排试听</Button>
                  <Button disabled trailingIcon="a-arrow-down" type="default">批量操作</Button>
                  <Button disabled type="default">学员预导入课程</Button>
                </div>
                <Popover align="end" content={<div className={styles.fieldOptions}>
                  {optionalFields.map((field) => <Checkbox checked={visibleFields.includes(field.key)} key={field.key} label={field.label} onCheckedChange={(checked) => setVisibleFields((current) => checked ? [...current, field.key] : current.filter((key) => key !== field.key))} />)}
                  <Button onClick={() => setVisibleFields(optionalFields.map((field) => field.key))} size="small" type="default">恢复默认</Button>
                </div>} title="自定义显示字段">
                  <Button type="default">自定义显示字段</Button>
                </Popover>
              </div>
              <Table
                ariaLabel="学员信息列表"
                className={styles.table}
                columns={columns.filter((column) => column.key === 'name' || column.key === 'actions' || visibleFields.includes(column.key))}
                emptyText="暂无匹配学员"
                onSelectionChange={setSelectedKeys}
                onSortChange={(key, order) => { setSort({ key, order }); setPage(1); setSelectedKeys([]); }}
                pagination={{
                  footerStart: selectedKeys.length > 0 ? `已选 ${selectedKeys.length} 人（仅当前页）` : undefined,
                  onPageChange: (next) => { setPage(next); setSelectedKeys([]); },
                  onPageSizeChange: (next, size) => { setPage(next); setPageSize(size); setSelectedKeys([]); },
                  page, pageSize, pageSizeOptions: [10, 20],
                }}
                rowKey={(student) => student.id}
                rows={rows}
                selectable
                selectedKeys={selectedKeys}
                sortKey={sort.key}
                sortOrder={sort.order}
                tableLayout="fixed"
              />
            </> },
            { id: 'student-communication', label: '学员沟通管理', disabled: true },
          ]} />
        </PageContentCard>
      </section>
    </div>
    <Dialog footer={<Button onClick={() => setDetail(null)} type="default">关闭</Button>} onOpenChange={(open) => { if (!open) setDetail(null); }} open={detail !== null} title="学员信息" width={560}>
      {detail ? <>
        <div className={styles.detailHeading}><StudentAvatar student={detail} /><h2>{detail.name}</h2><span>{detail.status}</span></div>
        <dl className={styles.details}>
          {[
            ['学号', detail.studentNo], ['师生信账号', detail.account], ['性别', detail.gender], ['手机号码', maskStudentPhone(detail.phone)],
            ['英文名', detail.englishName], ['身份证号', detail.identityNo || '—'], ['卡号', detail.cardNo || '—'],
            ['公立学校', detail.school], ['所属校区', detail.campus], ['学员类型', detail.isFormal ? '正式学员' : '非正式学员'],
          ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl>
      </> : null}
    </Dialog>
  </main>;
}
